import { useRef, useState, useCallback } from 'react';
import { 
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Flex,
	Image,
	Text,
	useToast,
} from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import * as Yup from 'yup';

import api from '../../../services/api';
import { formatPrice } from '../../../utils/format';
import getValidationErrors from '../../../utils/getValidationErrors';
import InputTextArea from '../../Inputs/inputTextArea';
import { FinJsonDTO } from '../../../dtos/FinJsonDTO';

import {
	Container,
	Content,
	Header,
	TituloContent,
	TituloItem,
	FormView,
	Buttons,
} from './styles';

interface ModalTituloDeleteParams {
	id?: string;
	onClose: any;
	isVisible: boolean;
	titulo: FinJsonDTO;
	handleForm: () => Promise<void>;
}

const ModalTituloDelete: React.FC<ModalTituloDeleteParams> = ({
	id = 'modalTituloDelete',
	onClose = () => {},
	isVisible,
	titulo,
	handleForm,
}) => {
	const formRef = useRef<FormHandles>(null);
	const toast = useToast();

	const handleOutSideClick = useCallback(
		(e: any) => {
			if (e.target.id === id) {
				onClose();
			}
		},
		[id, onClose],
	);

	const [visible, setVisible] = useState<boolean>(isVisible);

	const [descriptionLength, setDescriptionLength] = useState(0);

	async function handleSubmit(form: any) {
		try {
			formRef.current?.setErrors({});

			const schema = Yup.object().shape({
				motivo: Yup.string().required('Motivo da exclusão obrigatório'),
			});

			await schema.validate(form, {
				abortEarly: false,
			});

			const { motivo } = form;

			await api.post('/portal/financeiro/delete', { financeiro: titulo, motivo })
				.then(response => {
					toast({
						title: 'Exclusão do Titulo',
						description: response.data.mensagem,
						status: response.data.conf === 'S' ? 'success' : 'error',
						duration: 5000,
						position: 'top',
						isClosable: true,
						containerStyle: {
							marginTop: '5'
						}
					});
				})
				.finally(async () => {
					await handleForm();
					setVisible(false);
					onClose(false);
				});
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				const errors = getValidationErrors(err);
				formRef.current?.setErrors(errors);

				toast({
					title: 'ATENÇÃO',
					description: 'Obrigatório preencher o motivo da exclusão!',
					status: 'warning',
					duration: 2000,
					position: 'top-right',
					isClosable: true,
					containerStyle: {
						marginTop: '5'
					}
				});
			}
		}
	};

	return (
		<Container id="modalTituloDelete" onClick={handleOutSideClick}>
			<Content className={`${visible ? 'active' : ''}`}>
				<Header>
					<Text fontSize={22} fontWeight={600} color="red.800">Exclusão do Título</Text>
				</Header>

				<TituloContent>
					<TituloItem>
						<Flex>
							<h1>Cliente:</h1>&nbsp;&nbsp;
							<span>{titulo.cliente.nome}</span>
						</Flex>
					</TituloItem>

					<TituloItem>
						<h1>Título</h1>
						<Flex flexDir="column">
							<h2>Nro: <span>{titulo.doc}</span></h2>
							<h2>Data:
								<span>
									{format(new Date(titulo.data), " dd'/'M'/'Y", { locale: ptBR })}
								</span>
							</h2>

							<h2>Valor: <span>{formatPrice(titulo.vlr_doc)}</span></h2>
							<h2>Obs: <span>{titulo.obs}</span></h2>
						</Flex>
					</TituloItem>

					<TituloItem>
						<h1>Pagamento</h1>
						<Flex>
							<Image src={titulo.pagamentos.url_pay} objectFit='cover' w='30px' h="25px" />
							<h2>{titulo.pagamentos.name_pay}</h2>
						</Flex>
					</TituloItem>

					{titulo.recorrencia?.ativo && (
						<TituloItem>
							<h1>Recorrencia</h1>
							<h2>{titulo.recorrencia.dia}</h2>
							<h2>{titulo.recorrencia.fim}</h2>
							<h2>{titulo.recorrencia.tempo}</h2>
						</TituloItem>
					)}

					<TituloItem>
						<h1>Operação</h1>
						<h2>{titulo.operacao.nome}</h2>
						<h2>{titulo.operacao.type_nome}</h2>
					</TituloItem>

					{(titulo.parcelas && titulo.parcelas.length > 0) && (
						<TituloItem>
							<h1>Parcelas</h1>
							<Accordion allowMultiple>
								{titulo.parcelas.map(parcela => {
									return (
										<AccordionItem
											key={parcela.id}
											border={0}
											bg="gray.50"
											borderRadius={4}
											mb={2}
										>
											<AccordionButton pl={0} pr={2} _hover={{ background: "transparent" }}>
												<Box as="h2" flex='1' textAlign='left'>
													Parcela: <span>{parcela.parcela}</span>&nbsp;
													Venc: <span>
														{format(new Date(parcela.vencimento),
															" dd'/'M'/'Y", { locale: ptBR }
														)}
													</span>
												</Box>
												<AccordionIcon color="blue.500" />
											</AccordionButton>

											<AccordionPanel px={2} pt={-2} pb={4}>
												<Flex>
													<h2>Operação: <span>{parcela.vlr_cred > 0 ? 'Crédito' : 'Débito'}</span></h2>
													<h2>Valor: <span>{formatPrice(parcela.vlr_cred + parcela.vlr_deb)}</span></h2>
												</Flex>

												<h2>Desconto: <span>{formatPrice(parcela.desconto)}</span></h2>
												<h2>Pago: <span>{formatPrice(parcela.pago)}</span></h2>
												<h2>Amortizado: <span>{formatPrice(parcela.amortizado)}</span></h2>

												<Flex>
													<h2>Juros: <span>{formatPrice(parcela.juros)}</span></h2>
													<h2>Multa: <span>{formatPrice(parcela.multa)}</span></h2>
												</Flex>

												<Flex>
													<h2>Taxa Cartão: <span>{formatPrice(parcela.taxa_card)}</span></h2>
													<h2>Taxa Antecipação: <span>{formatPrice(parcela.taxa_antecip)}</span></h2>
												</Flex>

												<h2>Outra Despesa: <span>{formatPrice(parcela.outra_desp)}</span></h2>

												<Flex>
													<h2>Saldo: <span>{formatPrice(parcela.saldo_cred + parcela.saldo_deb)}</span></h2>
												</Flex>

												{parcela.data_baixa && (
													<h2>Data Baixa:
														<span>
															{format(new Date(parcela.data_baixa),
																" dd'/'M'/'Y", { locale: ptBR }
															)}
														</span>
													</h2>
												)}

												{parcela.boleto && (
													<Flex flexDir="column">
														<Text color="gray.700" fontWeight={500}>Boleto:</Text>

														<Flex
															flexDir="column"
															borderWidth={2}
															borderColor="gray.100"
															borderRadius={8}
															ml={2}
															py={1}
															mb={1}
														>
															<h2>Nosso Nro: <span>{parcela.boleto.nosso_nro}</span></h2>
															<h2>Linha Dig:&nbsp;
																<Text>
																	<span style={{ color: '#1a83ff' }}>{parcela.boleto.linha_dig}</span>
																</Text>
															</h2>
															<h2>Status: <span>{parcela.boleto.registro}</span></h2>
															{parcela.boleto.venc && (
																<h2>Vencimento:
																	<span>
																		{format(new Date(parcela.boleto.venc),
																			" dd'/'M'/'Y", { locale: ptBR }
																		)}
																	</span>
																</h2>
															)}
															<h2>Valor: <span>{formatPrice(parcela.boleto.vlr)}</span></h2>
															<Flex>
																<h2>Multa: <span>{formatPrice(parcela.boleto.multa)}</span></h2>
																<h2>Mora: <span>{formatPrice(parcela.boleto.mora)}</span></h2>
															</Flex>
														</Flex>
													</Flex>
												)}
											</AccordionPanel>
										</AccordionItem>
									)
								})}
							</Accordion>
						</TituloItem>
					)}

					<FormView ref={formRef} onSubmit={() => {}}>
						{/* OBS */}
						<div className="input">
							<p>Motivo da Exclusão <span>(obrigatório)</span></p>
							<InputTextArea
								name="motivo"
								placeholder="Motivo da exclusão"
								rows={3}
								cols={4}
								max={5000}
								onChange={(e: any) => setDescriptionLength(e.target.value.length)}
								containerStyle={{ padding: '6px 0 6px 10px' }}
							/>

							<span>
								{descriptionLength}/5000
								caracteres
							</span>
						</div>
					</FormView>
				</TituloContent>

				<Buttons>
					<Flex flexDir="column" pl={4} py={0}>
						<Text fontSize={14} fontWeight={400}>Usuário:</Text>
						<Text pl={2} fontSize={15} fontWeight={500}>{titulo.user_nome}</Text>
					</Flex>

					<Flex>
						<button
							type="button"
							className="button-cancel"
							onClick={() => onClose(false)}
						>
							Cancelar
						</button>

						<button
							type="submit"
							className="button-excluir"
							onClick={() => handleSubmit(formRef.current?.getData())}
						>
							&nbsp;&nbsp;&nbsp;Excluir&nbsp;&nbsp;&nbsp;
						</button>
					</Flex>
				</Buttons>
			</Content>
		</Container>
	)
}

export default ModalTituloDelete;